import React, { useState, useEffect, useRef } from "react";
import { Header } from "../../Header";
import { Link } from "react-router-dom";
import CircularProgress from '@mui/material/CircularProgress';
import './Customer.css';
import { local_url, local_signage } from "../../Const";
import { FormControl, InputLabel, Select, MenuItem, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

// Helper functions remain the same
const formatDateForInput = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateForAPI = (date) => date.replace(/-/g, '');

const calculateDateDifference = (from, to) => {
  const fromDate = new Date(from);
  const toDate = new Date(to);
  return Math.floor((toDate - fromDate) / (1000 * 60 * 60 * 24));
};

// Create a session storage key
const SESSION_STORAGE_KEY = 'customerAgeingLedgerState';

export const CustomerAgeingLedger = () => {
  const today = new Date();
  const fifteenDaysAgo = new Date(today);
  fifteenDaysAgo.setDate(today.getDate() - 30);
  const sixtyDaysAgo = new Date(today);
  sixtyDaysAgo.setDate(today.getDate() - 90);

  // Initialize state from session storage or default values
  const getInitialState = () => {
    const savedState = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (savedState) {
      const parsed = JSON.parse(savedState);
      return {
        ledgerData: parsed.ledgerData || [],
        fromDate: parsed.fromDate || formatDateForInput(fifteenDaysAgo),
        toDate: parsed.toDate || formatDateForInput(today),
        branchType: parsed.branchType || "",
        area: parsed.area || "ALL",
        selectedBranch: parsed.selectedBranch || null,
        branchInputValue: parsed.branchInputValue || "",
      };
    }
    return {
      ledgerData: [],
      fromDate: formatDateForInput(fifteenDaysAgo),
      toDate: formatDateForInput(today),
      branchType: "",
      area: "ALL",
      selectedBranch: null,
      branchInputValue: "",
    };
  };

  const initialState = getInitialState();

  // Dropdown filters
  const [segments, setSegments] = useState([]);
  const [branchType, setBranchType] = useState(initialState.branchType);
  const [areas, setAreas] = useState([]);
  const [area, setArea] = useState(initialState.area);
  const [branchList, setBranchList] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(initialState.selectedBranch);
  const [branchInputValue, setBranchInputValue] = useState(initialState.branchInputValue);
  
  const [ledgerData, setLedgerData] = useState(initialState.ledgerData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [fromDate, setFromDate] = useState(initialState.fromDate);
  const [toDate, setToDate] = useState(initialState.toDate);

  const formattedFromDateForAPI = formatDateForAPI(fromDate);
  const formattedToDateForAPI = formatDateForAPI(toDate);

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null
  });

  const userId = localStorage.getItem('userId');
  const token = localStorage.getItem('AuthorizationToken');
  
  // Track if component is mounted
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Auth error handler
  const handleAuthError = () => {
    console.error("Session expired or unauthorized access.");
    localStorage.clear();
    sessionStorage.clear();
    alert("Session expired. Please log in again.");
    window.location.href = "/login";
  };

  // Dropdown handlers
  const handleBranchTypeChange = (event) => {
    const value = event.target.value;
    setBranchType(value);
    setSelectedBranch(null); // Reset branch selection when segment changes
    setBranchInputValue("");
    setIsSubmitDisabled(false);
    setLedgerData([]); // Reset table data when segment changes
  };

  const handleAreaChange = (event) => {
    const value = event.target.value;
    setArea(value);
    setSelectedBranch(null); // Reset branch selection when area changes
    setBranchInputValue("");
    setIsSubmitDisabled(false);
    setLedgerData([]); // Reset table data when area changes
  };

  const handleBranchChange = (event, newValue) => {
    setSelectedBranch(newValue);
    setIsSubmitDisabled(false);
    setLedgerData([]); // Reset table data when branch changes
  };

  // Date picker handlers
  const handleFromDateChange = (newValue) => {
    setFromDate(newValue.format('YYYY-MM-DD'));
    setIsSubmitDisabled(false);
  };

  const handleToDateChange = (newValue) => {
    setToDate(newValue.format('YYYY-MM-DD'));
    setIsSubmitDisabled(false);
  };

  // Save state to session storage whenever it changes
  useEffect(() => {
    if (isMounted.current) {
      const stateToSave = {
        ledgerData,
        fromDate,
        toDate,
        branchType,
        area,
        selectedBranch,
        branchInputValue,
      };
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(stateToSave));
    }
  }, [ledgerData, fromDate, toDate, branchType, area, selectedBranch, branchInputValue]);

  // Build API URL based on filters
  const buildApiUrl = () => {
    // Base area - use selected area or default
    const areaParam = area || "ALL";

    // Branch parameter - use selected branch ID or ALL
    const branchParam = selectedBranch ? selectedBranch.branchID : "ALL";

    return `${local_url}/api/CreditAgeReport/getCreditAgeSummary/${branchParam}/${areaParam}/${formattedFromDateForAPI}/${formattedToDateForAPI}`;
  };

  // Fetch business segments when component mounts
  useEffect(() => {
    const fetchSegments = async () => {
      try {
        const response = await fetch(`${local_url}/api/BizSegments`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            handleAuthError();
            return;
          }
          throw new Error('Failed to fetch business segments');
        }

        const data = await response.json();
        console.log('Fetched business segments:', data);

        // Filter out deleted segments
        const activeSegments = data.filter(segment => segment.deleted === "N");
        setSegments(activeSegments);
      } catch (error) {
        console.error('Error fetching business segments:', error);
      }
    };

    if (token) {
      fetchSegments();
    }
  }, [token]);

  // Fetch areas when segment changes or component mounts with saved segment
  useEffect(() => {
    const fetchAreas = async () => {
      if (!branchType) return;

      try {
        const response = await fetch(`${local_signage}/api/v1/sfareabysegment?SegmentCode=${branchType}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            handleAuthError();
            return;
          }
          throw new Error('Failed to fetch areas by segment');
        }

        const data = await response.json();
        console.log('Fetched areas for segment:', data);
        setAreas(data);

        // Only reset area if not restoring from session
        if (!initialState.area || initialState.area === "ALL") {
          setArea("ALL");
        }
      } catch (error) {
        console.error('Error fetching areas by segment:', error);
        setAreas([]);
      }
    };

    if (token && branchType) {
      fetchAreas();
    }
  }, [branchType, token]);

  // Fetch branch list when area changes or component mounts with saved area
  const fetchBranchList = async (fetchType, value) => {
    if (!token) {
      console.error('Authentication token is missing');
      handleAuthError();
      return;
    }

    try {
      // Use the selected area ID for API call
      const areaParam = area || "ALL";
      const url = `${local_url}/api/Company/GetBranchList/${areaParam}/SEGMENT/${value}`;

      console.log('Fetching branches from URL:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleAuthError();
          return;
        }
        throw new Error('Failed to fetch branch list');
      }

      const data = await response.json();
      console.log('Fetched branch list:', data);
      setBranchList(data);
      
      // If there's a saved branch in session storage, find and select it
      if (initialState.selectedBranch && data.length > 0) {
        const savedBranch = data.find(branch => 
          branch.branchID === initialState.selectedBranch.branchID
        );
        
        if (savedBranch) {
          setSelectedBranch(savedBranch);
          setBranchInputValue(savedBranch.branchName || "");
        }
      }
    } catch (error) {
      console.error('Error fetching branch list:', error);
      setBranchList([]);
    }
  };

  useEffect(() => {
    if (branchType && area) {
      fetchBranchList("AREA", branchType);
    }
  }, [area, branchType]);

  // Fetch data on initial load if we have required params
  useEffect(() => {
    if (initialState.branchType && initialState.ledgerData.length > 0) {
      // If we have stored data and filters, use them
      if (!isSubmitDisabled) {
        setIsSubmitDisabled(true);
      }
      
      // If we have all the necessary data from session storage, 
      // automatically fetch the data again
      if (initialState.branchType && initialState.fromDate && initialState.toDate) {
        // Small delay to ensure all other state is properly set
        setTimeout(() => {
          if (isMounted.current) {
            fetchLedgerData();
          }
        }, 500);
      }
    }
  }, []);

  const sortData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      if (sortConfig.key === "customerCode") {
        return sortConfig.direction === "ascending"
          ? a.customerCode.localeCompare(b.customerCode)
          : b.customerCode.localeCompare(a.customerCode);
      }
      if (sortConfig.key === "customerName") {
        return sortConfig.direction === "ascending"
          ? a.customerName.localeCompare(b.customerName)
          : b.customerName.localeCompare(a.customerName);
      }
      return 0;
    });
  };

  // Add this handler function
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === "ascending"
        ? "descending"
        : "ascending"
    }));
  };

  const fetchLedgerData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Retrieve token from localStorage
      const token = localStorage.getItem('AuthorizationToken');
      console.log('---', token);

      if (!token) {
        setError("Authorization token is missing. Please log in again.");
        localStorage.clear(); // Clear localStorage
        sessionStorage.clear(); // Clear sessionStorage
        window.location.href = "/login"; // Redirect to login page
        setLoading(false);
        return;
      }

      // Build API URL with filters
      const API_URL = buildApiUrl();
      console.log("Fetching from:", API_URL);

      // Make API call with Authorization header
      const response = await fetch(API_URL, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Check response status
      if (response.status === 401) {
        // Session expired or unauthorized access
        console.error("Session expired or unauthorized access.");
        localStorage.clear(); // Clear localStorage
        sessionStorage.clear(); // Clear sessionStorage
        alert("Session expired. Please log in again.");
        window.location.href = "/login"; // Redirect to login page
        return;
      }

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }

      // Parse response data
      const data = await response.json();
      setLedgerData(data);
    } catch (error) {
      console.error("Error fetching ledger data:", error);
      setError("Failed to fetch ledger data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    const dateDifference = calculateDateDifference(fromDate, toDate);

    if (dateDifference > 90) {
      setError("Date range cannot exceed 60 days. Please adjust the dates.");
      return;
    }

    setError(null);
    setIsSubmitDisabled(true);
    fetchLedgerData();
  };

  const handleDateChange = (setter) => (event) => {
    setter(event.target.value);
    setIsSubmitDisabled(false);
  };

  const formatDueSinceDate = (isoDate, fromDate) => {
    if (!isoDate) return 'NA';

    const dueDate = new Date(isoDate);
    const formattedDueDate = `${dueDate.getFullYear()}-${String(dueDate.getMonth() + 1).padStart(2, '0')}-${String(dueDate.getDate()).padStart(2, '0')}`;
    const today = new Date();
    const formattedToday = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    // If the date is today's date, return NA
    if (formattedDueDate === formattedToday) {
      return 'NA';
    }

    // Check if fromDate is the same as dueSince date
    if (formattedDueDate === fromDate) {
      return `Beyond ${formattedDueDate}`;
    }

    return `${String(dueDate.getDate()).padStart(2, '0')}-${String(dueDate.getMonth() + 1).padStart(2, '0')}-${dueDate.getFullYear()}`;
  };

  const calculateTotalCreditAmount = (item) => {
    return (item.upto7days || 0) +
      (item.upto14days || 0) +
      (item.upto21days || 0) +
      (item.above21days || 0);
  };

  const exportToCSV = () => {
    const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);

    const dataToExport = ledgerData.map(item => {
      const totalCreditAmount = calculateTotalCreditAmount(item);
      const availableCredit = item.creditLimit - totalCreditAmount;

      return {
        "Customer Id": item.customerCode,
        "Customer Name": item.customerName,
        "Credit Limit": formatCurrency(item.creditLimit.toFixed(2)),
        "Credit Days": item.creditDays,
        "Due Since": item.dueSince,
        "Exceed Days": item.exceedDays,
        "0-7 days": item.upto7days?.toFixed(2) || 0,
        "8-14 days": item.upto14days?.toFixed(2) || 0,
        "15-21 days": item.upto21days?.toFixed(2) || 0,
        ">21 days": item.above21days?.toFixed(2) || 0,
        "> credit days": item.exceedAmount?.toFixed(2) || 0,
        "Total Credit Amount": formatCurrency(totalCreditAmount.toFixed(2)),
        "Available Credit": formatCurrency(availableCredit.toFixed(2))
      };
    });

    const BOM = '\uFEFF';
    const csvRows = [];
    const headers = Object.keys(dataToExport[0]);
    csvRows.push(headers.join(','));

    for (const row of dataToExport) {
      const values = headers.map(header => `"${row[header]}"`);
      csvRows.push(values.join(','));
    }

    const csvString = BOM + csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', 'customer_ledger.csv');
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderTable = () => (
    <div className="customer-table-container">
      <div className="customer-table-scroll">
        <table className="customer-table">
          <thead>
            <tr>
              <th>S No.</th>
              <th onClick={() => handleSort("customerCode")} style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  Customer Id
                  <div>
                    <ArrowDropUpIcon
                      sx={{ opacity: sortConfig.key === "customerCode" && sortConfig.direction === "ascending" ? 1 : 0.3 }}
                    />
                    <ArrowDropDownIcon
                      sx={{ opacity: sortConfig.key === "customerCode" && sortConfig.direction === "descending" ? 1 : 0.3 }}
                    />
                  </div>
                </div>
              </th>
              <th onClick={() => handleSort("customerName")} style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  Customer Name
                  <div>
                    <ArrowDropUpIcon
                      sx={{ opacity: sortConfig.key === "customerName" && sortConfig.direction === "ascending" ? 1 : 0.3 }}
                    />
                    <ArrowDropDownIcon
                      sx={{ opacity: sortConfig.key === "customerName" && sortConfig.direction === "descending" ? 1 : 0.3 }}
                    />
                  </div>
                </div>
              </th>
              <th>Credit Limit</th>
              <th>Credit Days</th>
              <th>Due Since</th>
              <th>Exceed Days</th>
              <th>0-7 Days</th>
              <th>8-14 Days</th>
              <th>15-21 Days</th>
              <th>&gt;21 Days</th>
              <th>&gt; Credit Days</th>
              <th>Total Credit Amount</th>
              <th>Available Credit</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="13" className="customer-loading-container">
                  <CircularProgress />
                </td>
              </tr>
            ) : sortData(ledgerData).length > 0 ? (
              sortData(ledgerData).map((item, index) => {
                const totalCreditAmount = calculateTotalCreditAmount(item);
                const availableCredit = item.creditLimit - totalCreditAmount;

                // Determine the "Exceed Days" display and style
                let exceedDays =
                  formatDateForInput(fromDate) === formatDateForInput(item.dueSince)
                    ? `${item.exceedDays}+`
                    : formatDateForInput(item.dueSince) === formatDateForInput(today)
                      ? 'NA'
                      : item.exceedDays;

                // Convert exceedDays to a string and check if it ends with '+'
                const exceedDaysString = exceedDays != null ? String(exceedDays) : 'NA';
                const isRed = exceedDaysString.endsWith('+');

                return (
                  <tr key={item.customerCode}>
                    <td>{index + 1}</td> {/* Using the index here to display serial number */}
                    <td>
                      <Link to={`/customer-detail/${item.customerCode}/${formattedFromDateForAPI}/${formattedToDateForAPI}/${selectedBranch ? selectedBranch.branchID : 'ALL'}`}>
                        {item.customerCode}
                      </Link>
                    </td>
                    <td>{item.customerName}</td>
                    <td>₹{item.creditLimit.toFixed(2)}</td>
                    <td>{item.creditDays}</td>
                    <td>{formatDueSinceDate(item.dueSince, fromDate)}</td>
                    <td className={isRed ? 'red-text' : ''}>{exceedDaysString}</td>
                    <td>₹{(item.upto7days || 0).toFixed(2)}</td>
                    <td>₹{(item.upto14days || 0).toFixed(2)}</td>
                    <td>₹{(item.upto21days || 0).toFixed(2)}</td>
                    <td>₹{(item.above21days || 0).toFixed(2)}</td>
                    <td>₹{(item.exceedAmount || 0).toFixed(2)}</td>
                    <td>₹{totalCreditAmount.toFixed(2)}</td>
                    <td>₹{availableCredit.toFixed(2)}</td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="13" className="customer-no-data">
                  No ledger data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <>
      <Header />
      <div className="customer-container">
        {error && (
          <div className="customer-error-message">
            {error}
          </div>
        )}

        <div className="customerage-date-input-containe" style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Branch Type (Segment) Dropdown */}
          <FormControl variant="outlined" style={{ minWidth: 150 }}>
            <InputLabel id="branch-type-select-label">Segment</InputLabel>
            <Select
              labelId="branch-type-select-label"
              value={branchType}
              onChange={handleBranchTypeChange}
              label="Segment"
            >
              {segments.map((segment) => (
                <MenuItem key={segment.businessSegmentCode} value={segment.businessSegmentCode}>
                  {segment.businessSegmentName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Area Dropdown */}
          <FormControl variant="outlined" style={{ minWidth: 130, maxWidth: 130 }}>
            <InputLabel id="area-select-label">Area</InputLabel>
            <Select
              labelId="area-select-label"
              value={area}
              onChange={handleAreaChange}
              label="Area"
              disabled
            >
              <MenuItem value="ALL">All</MenuItem>
              {areas.map((area) => (
                <MenuItem key={area.SFAreaId} value={area.SFAreaId}>
                  {area.SFAreaName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Branch Autocomplete */}
          {branchType && (
            <Autocomplete
              style={{ minWidth: 180, maxWidth: 180 }}
              options={branchList || []}
              getOptionLabel={(option) => option.branchName || ""}
              value={selectedBranch}
              onChange={handleBranchChange}
              inputValue={branchInputValue}
              onInputChange={(event, newInputValue) => {
                setBranchInputValue(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Branch"
                  variant="outlined"
                  size="medium"
                />
              )}
              isOptionEqualToValue={(option, value) => option.branchID === value?.branchID}
            />
          )}

          {/* Date Pickers */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="From Date"
              value={dayjs(fromDate)}
              onChange={handleFromDateChange}
              format="DD-MM-YYYY"
              slotProps={{
                textField: {
                  size: 'small',
                  sx: {
                    width: 150,
                    backgroundColor: '#F1F8E9',
                    '& .MuiOutlinedInput-root': {
                      height: 40,
                    }
                  },
                },
              }}
            />
          </LocalizationProvider>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="To Date"
              value={dayjs(toDate)}
              onChange={handleToDateChange}
              format="DD-MM-YYYY"
              slotProps={{
                textField: {
                  size: 'small',
                  sx: {
                    width: 150,
                    justifyContent: 'center',
                    backgroundColor: '#F1F8E9',
                    '& .MuiOutlinedInput-root': {
                      justifyContent: 'center',
                      height: 40,
                    }
                  },
                },
              }}
            />
          </LocalizationProvider>

          <button
            onClick={handleSubmit}
            className="customer-submit-button"
            disabled={loading || (!branchType) || isSubmitDisabled}
          >
            Submit
          </button>

          <button
            onClick={exportToCSV}
            className="customer-submit-button"
            disabled={loading || ledgerData.length === 0}
          >
            Export
          </button>
        </div>

        <div className="customer-info-row">
          <strong>To see the Detailed Customer Ledger, Click the Particular Customer ID</strong>
        </div>

        {/* Render the table */}
        {renderTable()}
      </div>
    </>
  );
};

export default CustomerAgeingLedger;